import React, { memo, useCallback, useMemo } from 'react'

import { FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, shape, string } from 'prop-types'

import { SHORTCUTS } from 'Pods/SearchPage/Components/QuickFilters/constants'
import { rememberAssetType } from 'Pods/SearchPage/Shared/RememberedAssetType/rememberedAssetType'
import { FILTER_NAME, getFeatureFilter, SELECTION_METHOD } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { setAssetType } from 'Pods/SearchPage/Store/actions/assetType'
import { setPersonalFilter } from 'Pods/SearchPage/Store/actions/moreFilters'
import { selectSubscriptionTier, toggleSubscriptionTier } from 'Pods/SearchPage/Store/actions/pricing'
import { PERSONAL_FILTER, SUBSCRIPTION_TIER } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

import { IN_MY_PLAN, IN_MY_WISH_LIST } from './constants'

// The "All" quick filters the bar hides at this width (Figma 3135:158259), with the bar's
// behavior: a shortcut switches the type and applies its filter, so it never shows checked here;
// the personal ones toggle in place, like PlanChip and WishListChip.
function QuickFeatures({ items }) {
  const translate = useTranslate()
  const trackFilterSelect = useTrackFilterSelect()

  const planTier = items.find(item => item.value === IN_MY_PLAN)?.planTier
  // A subscription search with no plan picked spans every tier, which is what Premium shows.
  const isPlanChecked = useSearchPageStore((state) => {
    if (!planTier || !state.withSubscription) return false
    if (!state.subscriptionTiers?.length) return planTier === SUBSCRIPTION_TIER.PREMIUM

    return state.subscriptionTiers.includes(planTier)
  })
  const isWishListChecked = useSearchPageStore(state => state.personalFilter === PERSONAL_FILTER.WISH_LIST_ONLY)

  const selectedFilters = useMemo(() => [
    ...(isPlanChecked ? [IN_MY_PLAN] : []),
    ...(isWishListChecked ? [IN_MY_WISH_LIST] : []),
  ], [isPlanChecked, isWishListChecked])

  const config = useMemo(() => [{
    label: '',
    filters: items.map(({ value, labelKey }) => ({ value, label: translate(labelKey) })),
  }], [items, translate])

  const handleChange = useCallback((_, value, isOn) => {
    if (value === IN_MY_PLAN) {
      if (!isOn) {
        toggleSubscriptionTier(planTier, false)
        return
      }
      trackFilterSelect(FILTER_NAME.PRICE, `subscription_tier_${planTier}`)
      selectSubscriptionTier(planTier)
      return
    }

    if (value === IN_MY_WISH_LIST) {
      if (isOn) trackFilterSelect(FILTER_NAME.PERSONAL, PERSONAL_FILTER.WISH_LIST_ONLY)
      setPersonalFilter(isOn ? PERSONAL_FILTER.WISH_LIST_ONLY : undefined)
      return
    }

    const shortcut = SHORTCUTS.find(item => item.key === value)
    if (!shortcut || !isOn) return

    // Same order as ShortcutChip: setAssetType resets the type's filters, so apply runs after it.
    setAssetType(shortcut.assetType)
    shortcut.apply()
    rememberAssetType(shortcut.assetType)

    trackFilterSelect(FILTER_NAME.PRODUCT_TYPE_TOGGLE, shortcut.assetType, SELECTION_METHOD.SHORTCUT)
    const { name, value: filterValue } = getFeatureFilter(shortcut.key)
    trackFilterSelect(name, filterValue)
  }, [planTier, trackFilterSelect])

  return (
    <FiltersSidebar
      isCollapsible={false}
      filters={config}
      selectedFilters={selectedFilters}
      onFiltersChange={handleChange}
      limit={0}
      isControlled
      useNewCheckboxes
      hideShowMore
      hideLastDivider
      baseFormWrapperClassName="filter-wrapper__content"
    />
  )
}

QuickFeatures.propTypes = {
  items: arrayOf(shape({
    value: string.isRequired,
    labelKey: string.isRequired,
    isHiddenAtSm: bool,
    planTier: string,
  })).isRequired,
}

export default memo(QuickFeatures)
